/**
 * Budgets and rollover.
 *
 * The effective budget for a period is `base + carried`, where **both are
 * derived** (INV-2's sibling rule). Nothing about a period is stored, so changing
 * the rollover mode or the cut-off day recomputes history rather than rewriting
 * it — and a user can flip the setting back and get their old numbers exactly.
 */

import { type Cents, ZERO, cents, sumCents } from "./money";
import { versionInForce } from "./versions";
import { type Period, nextPeriod, periodBounds, previousPeriod } from "./period";

export type RolloverMode = "none" | "surplus" | "all";

/**
 * How far back the carry walk will go. Ten years of monthly periods.
 *
 * This is a guard against a corrupt `occurredAt` (a row dated 1970, say)
 * turning a render into a 600-iteration loop — not a product limit. Anyone
 * hitting it has data that is wrong in a more interesting way.
 */
export const MAX_CARRY_PERIODS = 120;

/**
 * The shortest a period can be — February in a common year. Used only to turn a
 * span of milliseconds into an upper bound on the number of periods it holds.
 */
const SHORTEST_PERIOD_MS = 28 * 86_400_000;

export interface BudgetRow {
  readonly amountCents: Cents | null;
  /** `YYYY-MM-DD`. */
  readonly effectiveFrom: string;
}

export interface SpendRow {
  /** ISO instant. */
  readonly occurredAt: string;
  readonly amountCents: Cents;
}

export interface CarryContext {
  /** Budget versions for ONE category, any order. */
  readonly budgets: readonly BudgetRow[];
  /** Expenses for that same category, any order, tombstones already excluded. */
  readonly expenses: readonly SpendRow[];
  readonly cycleStartDay: number;
  readonly timeZone: string;
  readonly mode: RolloverMode;
}

/** The category's own mode if it has one, otherwise the profile default. */
export function resolveRolloverMode(
  profileMode: RolloverMode,
  categoryOverride: RolloverMode | null,
): RolloverMode {
  return categoryOverride ?? profileMode;
}

/**
 * The budget in force for a period, ignoring rollover: the latest version whose
 * `effectiveFrom` is on or before the period's first day.
 *
 * Returns null when no budget is set, including a selected cleared version — which is the normal state for
 * a new user, and must render as "no high-water mark" rather than as zero. A
 * zero budget means "spend nothing"; no budget means "not tracked". Collapsing
 * the two would show every new user as instantly over budget.
 */
export function baseBudget(
  budgets: readonly BudgetRow[],
  period: Period,
  timeZone: string,
): Cents | null {
  return versionInForce(budgets, period, timeZone)?.amountCents ?? null;
}

/**
 * The sum of the budgets in force across `periods` — the whole-history
 * high-water mark, for the Ledger's All-time scope.
 *
 * Null when no budget was ever in force in any of them, which must render as "no
 * high-water mark" for exactly the reason {@link baseBudget} gives: no budget
 * means "not tracked", not "spend nothing". A period with no budget contributes
 * nothing and does not make the whole answer null — a user who started budgeting
 * in March still has a defensible figure for March onwards.
 *
 * **Rollover is deliberately not summed.** Carry moves allowance *between*
 * periods; adding it to a total that already contains every period's base would
 * count the same dollar in the period that granted it and again in the period it
 * was carried into. So the figure this returns is exactly "the budget you were
 * ever given", which is a sentence that can be defended — and it is unaffected by
 * the rollover mode, as it should be.
 */
export function sumBaseBudgets(
  budgets: readonly BudgetRow[],
  periods: readonly Period[],
  timeZone: string,
): Cents | null {
  let total = 0;
  let any = false;

  for (const period of periods) {
    const base = baseBudget(budgets, period, timeZone);
    if (base === null) continue;
    any = true;
    total += base;
  }
  return any ? cents(total) : null;
}

/** Total spend falling inside a period. */
export function spentInPeriod(expenses: readonly SpendRow[], period: Period): Cents {
  const from = period.start.getTime();
  const to = period.end.getTime();
  const inside: Cents[] = [];

  for (const e of expenses) {
    const t = new Date(e.occurredAt).getTime();
    if (t >= from && t < to) inside.push(e.amountCents);
  }
  return sumCents(inside);
}

/**
 * Where the carry walk begins: the period containing this category's FIRST
 * EXPENSE — not the first budget.
 *
 * > Rollover accrues from your first logged expense in a category, never from
 * > the date a budget was set.
 *
 * The distinction is the difference between a sensible number and an absurd one.
 * A user who sets a $500 budget in January and logs nothing until June was not
 * underspending for five months — they were not using the app. Walking from the
 * budget date would hand them $2,500 of surplus the first time they switched
 * rollover on, which is both wrong and alarming.
 *
 * Once the walk has started, zero-spend periods DO carry their full budget:
 * "I budgeted $100 for Health and spent nothing" is real underspend.
 */
function walkStart(ctx: CarryContext, target: Period): Period | null {
  let earliest: number | null = null;
  for (const e of ctx.expenses) {
    const t = new Date(e.occurredAt).getTime();
    if (Number.isFinite(t) && (earliest === null || t < earliest)) earliest = t;
  }
  if (earliest === null) return null;

  const first = periodBounds(new Date(earliest), ctx.cycleStartDay, ctx.timeZone);

  // Bound the walk to MAX_CARRY_PERIODS before the target, so one corrupt
  // `occurred_at` (a row dated 1970) costs a few periods of ancient carry
  // rather than hanging the render.
  let floor = target;
  for (let i = 0; i < MAX_CARRY_PERIODS; i++) {
    const earlier = previousPeriod(floor, ctx.cycleStartDay, ctx.timeZone);
    if (earlier.start.getTime() <= first.start.getTime()) return first;
    floor = earlier;
  }
  return floor;
}

function clampCarry(leftover: number, mode: RolloverMode): number {
  switch (mode) {
    case "none":
      return 0;
    case "surplus":
      // Underspend carries forward; overspend does not follow you around.
      return Math.max(0, leftover);
    case "all":
      return leftover;
  }
}

/**
 * The amount carried INTO each of `periods`, in **one** forward walk.
 *
 * Walks forward from the first period with any activity, accumulating
 * `carry + base − spent` under the chosen mode and snapshotting the carry as it
 * passes each requested boundary. Forward rather than recursive, so the cost is
 * linear and there is no stack to blow.
 *
 * A period with no budget set contributes no carry — you cannot underspend a
 * budget that did not exist.
 *
 * **Why this is batched** (ROADMAP 8.8). Anything showing several periods at
 * once — the Ledger's period picker, the analytics period grain — used to make
 * one `carriedAmount` call per period, each re-walking history from the first
 * expense. That is superlinear in the number of periods and cost ~1s at 60
 * periods with rollover on. One walk snapshots them all.
 *
 * `periods` must be periods of the **same cut-off day and timezone** as `ctx`
 * (any order, sparse or contiguous; duplicates are fine). One that is not never
 * lands on this walk's boundaries, so it throws rather than returning a zero
 * carry that would render as a fact (INV-7).
 */
export function carriedAmounts(
  ctx: CarryContext,
  periods: readonly Period[],
): Map<number, Cents> {
  const carried = new Map<number, Cents>();
  for (const period of periods) carried.set(period.start.getTime(), ZERO);
  if (ctx.mode === "none" || carried.size === 0) return carried;

  const wanted = [...carried.keys()].sort((a, b) => a - b);
  const earliest = new Date(wanted[0]!);
  const latest = wanted[wanted.length - 1]!;

  // The floor comes from the EARLIEST requested period, so every later one is
  // reached by a walk that started at least as far back as its own would have.
  const first = walkStart(ctx, periodBounds(earliest, ctx.cycleStartDay, ctx.timeZone));
  if (!first) return carried;

  // Sort once and sweep with a moving pointer. The obvious implementation calls
  // `spentInPeriod` inside the loop, which rescans every expense for every
  // period — O(periods × expenses) per category, and this runs on every render
  // of the home screen. Sorting once makes the whole walk O(n log n + periods).
  const timeline = ctx.expenses
    .map((e) => ({ t: new Date(e.occurredAt).getTime(), amount: e.amountCents }))
    .filter((e) => Number.isFinite(e.t))
    .sort((a, b) => a.t - b.t);

  let cursorIndex = 0;
  // Skip anything before the walk starts — it is outside the bounded window.
  while (cursorIndex < timeline.length && timeline[cursorIndex]!.t < first.start.getTime()) {
    cursorIndex += 1;
  }

  let carry = 0;
  let cursor = first;
  const visited = new Set<number>();

  // Two stretches, bounded separately. The pre-history one is capped by
  // `walkStart` at MAX_CARRY_PERIODS; the requested one is bounded by its own
  // **span**, not by how many periods were asked for — a sparse list of two
  // periods five years apart is 2 entries and ~60 steps, and counting entries
  // would stop the walk short and then misreport it as a cut-off mismatch.
  const requestedSpan = Math.ceil((latest - wanted[0]!) / SHORTEST_PERIOD_MS) + 1;

  for (let i = 0; i < MAX_CARRY_PERIODS + requestedSpan; i++) {
    const at = cursor.start.getTime();
    if (carried.has(at)) {
      carried.set(at, cents(carry));
      visited.add(at);
    }
    if (at >= latest) break;

    const periodEnd = cursor.end.getTime();
    let spent = 0;
    while (cursorIndex < timeline.length && timeline[cursorIndex]!.t < periodEnd) {
      spent += timeline[cursorIndex]!.amount;
      cursorIndex += 1;
    }

    const base = baseBudget(ctx.budgets, cursor, ctx.timeZone);
    // No budget that period: nothing to under- or overspend against.
    carry = base === null ? 0 : clampCarry(carry + base - spent, ctx.mode);

    cursor = nextPeriod(cursor, ctx.cycleStartDay, ctx.timeZone);
  }

  // Anything at or after the walk's start that the walk never landed on is a
  // period from a different cycle. Zero would be a wrong budget shown as fact.
  const missed = wanted.filter((at) => at >= first.start.getTime() && !visited.has(at));
  if (missed.length > 0) {
    throw new Error(
      `${String(missed.length)} period(s) do not lie on the cut-off day ${String(ctx.cycleStartDay)} ` +
        `cycle in ${ctx.timeZone}, so no carry can be computed for them ` +
        `(first: ${new Date(missed[0]!).toISOString()})`,
    );
  }

  return carried;
}

/**
 * The amount carried INTO `period` from everything before it.
 *
 * A single-element wrapper over {@link carriedAmounts}, so there is exactly one
 * implementation of the walk.
 */
export function carriedAmount(ctx: CarryContext, period: Period): Cents {
  return carriedAmounts(ctx, [period]).get(period.start.getTime()) ?? ZERO;
}

/**
 * What the user may actually spend this period: base plus anything carried in.
 * Null when no budget is set for the category, including a selected cleared version.
 */
export function effectiveBudget(ctx: CarryContext, period: Period): Cents | null {
  const base = baseBudget(ctx.budgets, period, ctx.timeZone);
  if (base === null) return null;
  return cents(base + carriedAmount(ctx, period));
}

export interface BudgetStanding {
  /** Null when the category has no budget — render no high-water mark. */
  readonly budget: Cents | null;
  readonly spent: Cents;
  /** budget − spent. Negative means over. Null when untracked. */
  readonly remaining: Cents | null;
  /** 0..∞. 1 is exactly at the mark. Zero when untracked. */
  readonly ratio: number;
  readonly isOver: boolean;
  readonly carried: Cents;
}

/**
 * Everything a category row needs to render, for **many** periods at once.
 *
 * Shares one carry walk across all of them (ROADMAP 8.8), which is the whole
 * reason it exists. Keyed by `period.start.getTime()`.
 */
export function budgetStandings(
  ctx: CarryContext,
  periods: readonly Period[],
): Map<number, BudgetStanding> {
  const carried = carriedAmounts(ctx, periods);
  const standings = new Map<number, BudgetStanding>();

  for (const period of periods) {
    standings.set(period.start.getTime(), assemble(ctx, period, carried.get(period.start.getTime()) ?? ZERO));
  }
  return standings;
}

/** Everything a category row needs to render, in one pass. */
export function budgetStanding(ctx: CarryContext, period: Period): BudgetStanding {
  return assemble(ctx, period, carriedAmount(ctx, period));
}

/**
 * The one assembly of a standing from its three inputs, so the batched and
 * single forms can never drift on what "over" or "remaining" means.
 */
function assemble(ctx: CarryContext, period: Period, carried: Cents): BudgetStanding {
  const spent = spentInPeriod(ctx.expenses, period);
  const base = baseBudget(ctx.budgets, period, ctx.timeZone);

  if (base === null) {
    return { budget: null, spent, remaining: null, ratio: 0, isOver: false, carried: ZERO };
  }

  const budget = cents(base + carried);
  const remaining = cents(budget - spent);
  return {
    budget,
    spent,
    remaining,
    ratio: budget === 0 ? (spent > 0 ? Infinity : 0) : spent / budget,
    isOver: spent > budget,
    carried,
  };
}
